import { CliError } from "../../common.js";
import {
  createOperationalFailure,
  parseFailureClassification,
  type FailureClassification,
  type FailureContext,
  type OperationalFailure,
  type ProviderSafetyRating,
} from "../../failures.js";
import { LlmTransportError } from "../../llm/types.js";

type UnknownRecord = Record<string, unknown>;

export interface IngestErrorSidecar {
  schema_version: 1;
  unit: { kind: "episode"; episode: number };
  phase: string;
  occurred_at: string;
  failure: OperationalFailure;
  attempts: OperationalFailure[];
}

export type IngestStatusError = OperationalFailure & {
  pass: string;
  unit: string;
  phase: string;
  attempts: OperationalFailure[];
};

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function classificationForCliError(error: CliError): FailureClassification {
  switch (error.errorCode) {
    case "LLM_CREDENTIAL_MISSING":
      return { failure_type: "authentication", failure_code: "authentication_failed" };
    case "LLM_CONFIG_INVALID":
      return { failure_type: "provider", failure_code: "request_rejected" };
    case "INGEST_VIDEO_INLINE_TOO_LARGE":
      return { failure_type: "media", failure_code: "media_too_large" };
    case "INGEST_VIDEO_SERIAL_FAILED":
      return { failure_type: "pipeline", failure_code: "serial_parse_failed" };
    case "INGEST_VIDEO_TIMESTAMP_MISSING":
      return { failure_type: "validation", failure_code: "script_validation_failed" };
    case "INGEST_SOURCE_MISMATCH":
      return { failure_type: "pipeline", failure_code: "source_mismatch" };
    case "INGEST_PIPELINE_MISMATCH":
      return { failure_type: "pipeline", failure_code: "pipeline_mismatch" };
    case "INGEST_VALIDATION_FAILED":
      return { failure_type: "validation", failure_code: "script_validation_failed" };
    default:
      return { failure_type: "unknown", failure_code: "unknown" };
  }
}

export function failureFromError(error: unknown): { failure: OperationalFailure; attempts: OperationalFailure[] } {
  if (error instanceof LlmTransportError) {
    return { failure: error.failure, attempts: error.modelFailures };
  }
  if (error instanceof CliError) {
    const retryable = error.errorCode === "INGEST_VALIDATION_FAILED"
      || error.errorCode === "INGEST_VIDEO_SERIAL_FAILED";
    return {
      failure: createOperationalFailure(classificationForCliError(error), {
        retryable,
        message: error.message,
      }),
      attempts: [],
    };
  }
  return {
    failure: createOperationalFailure(
      { failure_type: "unknown", failure_code: "unknown" },
      { retryable: false, message: error instanceof Error ? error.message : String(error) },
    ),
    attempts: [],
  };
}

export function createEpisodeErrorSidecar(error: unknown, episode: number, phase: string): IngestErrorSidecar {
  const classified = failureFromError(error);
  return {
    schema_version: 1,
    unit: { kind: "episode", episode },
    phase,
    occurred_at: new Date().toISOString(),
    ...classified,
  };
}

export function recoveryHint(failure: OperationalFailure): string {
  if (failure.retryable) {
    return "Inspect the episode error sidecar and rerun `scriptctl ingest` to resume from this episode.";
  }
  if (failure.failure_type === "policy") {
    return "Inspect the provider policy reason in the episode error sidecar; rerunning the same source is unlikely to help.";
  }
  if (failure.failure_type === "authentication" || failure.failure_type === "authorization") {
    return "Fix the provider credential or permission reported in the episode error sidecar before rerunning.";
  }
  return "Inspect the typed episode error sidecar and resolve the terminal failure before rerunning.";
}

function oneOf<const Values extends readonly string[]>(value: unknown, values: Values): value is Values[number] {
  return typeof value === "string" && values.some((candidate) => candidate === value);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function parseSafetyRatings(value: unknown): ProviderSafetyRating[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const parsed: ProviderSafetyRating[] = [];
  for (const item of value) {
    if (!isRecord(item)) return undefined;
    if (typeof item["category"] !== "string" || typeof item["probability"] !== "string" || typeof item["blocked"] !== "boolean") {
      return undefined;
    }
    parsed.push({ category: item["category"], probability: item["probability"], blocked: item["blocked"] });
  }
  return parsed;
}

function parseFailureContext(value: UnknownRecord): FailureContext {
  const source = value["provider_source"];
  const reason = value["provider_reason"];
  const context: FailureContext = {
    provider: optionalString(value["provider"]),
    model: optionalString(value["model"]),
    operation: optionalString(value["operation"]),
    http_status: typeof value["http_status"] === "number" ? value["http_status"] : undefined,
    system_code: optionalString(value["system_code"]),
    provider_source: oneOf(source, ["prompt", "candidate"] as const) ? source : undefined,
    provider_reason: oneOf(reason, [
      "SAFETY", "PROHIBITED_CONTENT", "BLOCKLIST", "SPII", "RECITATION", "IMAGE_SAFETY",
      "IMAGE_PROHIBITED_CONTENT", "IMAGE_RECITATION", "ESCALATION", "OTHER", "UNSPECIFIED", "UNKNOWN",
    ] as const) ? reason : undefined,
    provider_reason_raw: optionalString(value["provider_reason_raw"]),
    finish_message: optionalString(value["finish_message"]),
    safety_ratings: parseSafetyRatings(value["safety_ratings"]),
    details: Array.isArray(value["details"]) && value["details"].every((detail) => typeof detail === "string")
      ? value["details"]
      : undefined,
  };
  return context;
}

export function parseOperationalFailure(value: unknown): OperationalFailure | null {
  if (!isRecord(value)) return null;
  const classification = parseFailureClassification(value["failure_type"], value["failure_code"]);
  if (!classification || typeof value["retryable"] !== "boolean" || typeof value["message"] !== "string") return null;
  return createOperationalFailure(classification, {
    ...parseFailureContext(value),
    retryable: value["retryable"],
    message: value["message"],
  });
}

export function parseIngestErrorSidecar(value: unknown): IngestErrorSidecar | null {
  if (!isRecord(value) || value["schema_version"] !== 1) return null;
  const unit = value["unit"];
  if (!isRecord(unit) || unit["kind"] !== "episode" || typeof unit["episode"] !== "number") return null;
  if (typeof value["phase"] !== "string" || typeof value["occurred_at"] !== "string") return null;
  const failure = parseOperationalFailure(value["failure"]);
  if (!failure || !Array.isArray(value["attempts"])) return null;
  const attempts: OperationalFailure[] = [];
  for (const item of value["attempts"]) {
    const attempt = parseOperationalFailure(item);
    if (!attempt) return null;
    attempts.push(attempt);
  }
  return {
    schema_version: 1,
    unit: { kind: "episode", episode: unit["episode"] },
    phase: value["phase"],
    occurred_at: value["occurred_at"],
    failure,
    attempts,
  };
}
